
import * as Sentry from '@sentry/vue'
import {
  Component,
  Prop,
  Vue,
  Watch,
} from 'vue-property-decorator'
import BISListDesktopForm from '@/components/bis_list/desktop_form.vue'
import BISListMobileForm from '@/components/bis_list/mobile_form.vue'
import BISListModify from '@/dataclasses/bis_list_modify'
import BISList from '@/interfaces/bis_list'
import { CharacterDetails } from '@/interfaces/character'
import {
  ExternalBISGearImportResponse,
  ImportError,
  LodestoneImportResponse,
  XIVGearSheetSelection,
} from '@/interfaces/imports'
import { BISListErrors } from '@/interfaces/responses'
import LoadCurrentGear from '@/components/modals/load_current_gear.vue'
import XIVGearSheetSetSelection from '@/components/modals/xivgear_sheet_set_selection.vue'

@Component({
  components: {
    BISListDesktopForm,
    BISListMobileForm,
  },
})
export default class BISListForm extends Vue {
  displayOffhand = true

  @Prop()
  bisList!: BISListModify

  @Prop()
  character!: CharacterDetails

  @Prop({ default: false })
  charIsProxy!: boolean

  etroImportPattern = /https:\/\/etro\.gg\/gearset\/([-a-z0-9]+)\/?/

  @Prop({ default() { return {} } })
  externalErrors!: BISListErrors

  importLoading = false

  internalErrors: BISListErrors = {}

  @Prop()
  method!: string

  @Prop({ default: true })
  renderDesktop!: boolean

  @Prop()
  url!: string

  xivGearImportPattern = /https:\/\/xivgear.app\/\?page=sl\|([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/

  // Set up default values for min and max IL, will change as new tiers are released
  maxIl = this.$store.state.maxItemLevel

  minIl = this.maxIl - 25

  get errors(): BISListErrors {
    return {
      ...this.internalErrors,
      ...this.externalErrors,
    }
  }

  get lodestoneImportUrl(): string {
    return `${process.env.VUE_APP_URL}/backend/api/lodestone/${this.character.lodestone_id}/import`
  }

  get syncableLists(): BISList[] {
    if (!this.renderDesktop || this.character == null || this.character.bis_lists == null) return []
    return this.character.bis_lists.filter((list: BISList) => list.id !== this.bisList.id && list.job.id === this.bisList.job_id)
  }

  calculateCurrentILRange(data: BISList): { minIl: number, maxIl: number } {
    const itemLevels = [
      data.current_mainhand.item_level,
      data.current_offhand.item_level,
      data.current_head.item_level,
      data.current_body.item_level,
      data.current_hands.item_level,
      data.current_legs.item_level,
      data.current_feet.item_level,
      data.current_earrings.item_level,
      data.current_necklace.item_level,
      data.current_bracelet.item_level,
      data.current_left_ring.item_level,
      data.current_right_ring.item_level,
    ]

    return { minIl: Math.min(...itemLevels), maxIl: Math.max(...itemLevels) }
  }

  displayLoadModal(): void {
    this.$modal.show(LoadCurrentGear, { bisLists: this.syncableLists, loadBIS: this.importCurrentData })
  }

  emitErrorCode(errorCode: number): void {
    this.$emit('error-code', errorCode)
  }

  async etroImport(): Promise<void> {
    const url = this.etroImportUrl()
    if (url === null) return
    this.importLoading = true
    try {
      const response = await fetch(url, { credentials: 'include' })
      if (response.ok) {
        // Handle the import
        const data = await response.json() as ExternalBISGearImportResponse
        this.importBISData(data)
      }
      else {
        const error = await response.json() as ImportError
        this.$notify({ text: `Error while importing Etro gearset; ${error.message}`, type: 'is-danger' })
      }
    }
    catch (e) {
      this.$notify({ text: `Error ${e} when attempting to import Etro data.`, type: 'is-danger' })
      Sentry.captureException(e)
    }
    finally {
      this.importLoading = false
    }
  }

  async xivGearImport(setNum: number): Promise<void> {
    let url = this.xivGearImportUrl()
    if (url === null) return

    if (setNum !== undefined) url = `${url}?set=${setNum}`
    this.importLoading = true
    try {
      const response = await fetch(url, { credentials: 'include' })
      if (response.status === 200) {
        // Handle the import
        const data = await response.json() as ExternalBISGearImportResponse
        this.importBISData(data)
      }
      else if (response.status === 202) {
        // Display a Modal to select the set you want to use, since the link is for a sheet.
        const data = await response.json() as XIVGearSheetSelection[]
        this.$modal.show(XIVGearSheetSetSelection, { doImport: this.xivGearImport, sheets: data })
      }
      else {
        const error = await response.json() as ImportError
        this.$notify({ text: `Error while importing XIVGear gearset; ${error.message}`, type: 'is-danger' })
      }
    }
    catch (e) {
      this.$notify({ text: `Error ${e} when attempting to import XIVGear data.`, type: 'is-danger' })
      Sentry.captureException(e)
    }
    finally {
      this.importLoading = false
    }
  }

  @Watch('bisList.external_link', { deep: true })
  etroImportUrl(): string | null {
    const match = this.etroImportPattern.exec(this.bisList.external_link || '')
    if (match === null) return null
    return `${process.env.VUE_APP_URL}/backend/api/import/etro/${match[1]}/`
  }

  @Watch('bisList.external_link', { deep: true })
  xivGearImportUrl(): string | null {
    const testUrl = this.bisList.external_link || ''
    if (testUrl === '') return null

    if (testUrl.startsWith('https://xivgear.app')) {
      this.bisList.external_link = decodeURI(testUrl)
    }

    const match = this.xivGearImportPattern.exec(testUrl)
    if (match === null) return null
    return `${process.env.VUE_APP_URL}/backend/api/import/xivgear/${match[1]}/`
  }

  handleErrors(errors: BISListErrors): void {
    this.internalErrors = errors
  }

  importBISData(data: ExternalBISGearImportResponse): void {
    if (data.min_il < this.minIl) this.minIl = data.min_il
    if (data.max_il > this.maxIl) this.maxIl = data.max_il
    Vue.nextTick(() => {
      this.bisList.importBIS(data)
      this.jobChange(data.job_id)
      this.$forceUpdate()
      this.$notify({ text: 'Successfully imported BIS Gear!', type: 'is-success' })
    })
  }

  importCurrentData(data: BISList): void {
    // Calculate min and max item levels
    const { minIl, maxIl } = this.calculateCurrentILRange(data)
    if (minIl < this.minIl) this.minIl = minIl
    if (maxIl > this.maxIl) this.maxIl = maxIl
    Vue.nextTick(() => {
      this.bisList.importCurrent(data)
      this.$forceUpdate()
      this.$notify({ text: 'Successfully imported Current Gear!', type: 'is-success' })
    })
  }

  importCurrentLodestoneGear(data: LodestoneImportResponse): void {
    if (data.min_il < this.minIl) this.minIl = data.min_il
    if (data.max_il > this.maxIl) this.maxIl = data.max_il
    Vue.nextTick(() => {
      this.bisList.importCurrentLodestoneGear(data)
      this.$forceUpdate()
      this.$notify({ text: 'Successfully imported Current Gear from Lodestone!', type: 'is-success' })
    })
  }

  jobChange(selectedJob: string): void {
    this.displayOffhand = selectedJob === 'PLD'
  }

  async lodestoneImport(): Promise<void> {
    this.importLoading = true
    try {
      const response = await fetch(`${this.lodestoneImportUrl}/${this.bisList.job_id}`, { credentials: 'include' })
      if (response.ok) {
        // Handle the import
        const data = await response.json() as LodestoneImportResponse
        this.importCurrentLodestoneGear(data)
      }
      else {
        const error = await response.json() as ImportError
        if (response.status === 406) {
          this.$notify({ text: error.message, type: 'is-link' })
        }
        else {
          // Only add the "Error while ..." text when it's not for the wrong job error
          this.$notify({ text: `Error while importing Lodestone gear; ${error.message}`, type: 'is-danger' })
        }
      }
    }
    catch (e) {
      this.$notify({ text: `Error ${e} when attempting to import Lodestone data.`, type: 'is-danger' })
      Sentry.captureException(e)
    }
    finally {
      this.importLoading = false
    }
  }

  mounted(): void {
    this.displayOffhand = this.bisList.job_id === 'PLD'
  }

  @Watch('bisList.job_id', { deep: true })
  syncable(): boolean {
    return this.syncableLists.length > 0
  }

  updateItemLevels(values: number[]): void {
    // Always comes in as [min, max]
    [this.minIl, this.maxIl] = values
  }
}
